import React, { useEffect, useMemo, useRef, useState } from "react";
import { COUNTRIES, ALL_CITIES, findCity } from "../data/locations";
import useGeoLocation from "../lib/useGeoLocation";
import { useI18n } from "../lib/i18n";
import { MapPin, ChevronDown, Search, X, Check, Locate } from "lucide-react";

/** Popular cities surfaced as chips at the top of the picker. */
const POPULAR_IDS = ["Riyadh", "Jeddah", "Makkah", "Madinah", "Dammam", "Khobar", "Dubai", "Abu-Dhabi", "Doha", "Kuwait-City", "Manama", "Muscat"];

/**
 * LocationPicker — cascading country → region → city selector with a
 * top-search bar that filters across every supported city instantly.
 *
 * Props:
 *   value:           currently selected city id (string) or ""
 *   onChange(id):    callback
 *   placeholder:     button label when nothing is selected
 *   compact:         smaller trigger for inline use
 *   testId:          base data-testid prefix
 */
export default function LocationPicker({
  value = "",
  onChange,
  placeholder,
  compact = false,
  testId = "location-picker",
  autoDetect = true,
}) {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const { detectedCityId, loading: detecting } = useGeoLocation({ autoDetect });
  const detected = useMemo(() => findCity(detectedCityId), [detectedCityId]);

  const selected = useMemo(() => findCity(value), [value]);

  // Close on click-outside
  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", handle);
    return () => window.removeEventListener("mousedown", handle);
  }, [open]);

  // Auto-focus search input on open
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery("");
    }
  }, [open]);

  const buttonLabel = selected
    ? `${selected.country.flag} ${lang === "ar" ? `${selected.ar} · ${selected.country.ar}` : `${selected.en} · ${selected.country.en}`}`
    : (placeholder || (lang === "ar" ? "اختر الموقع — الدولة / المنطقة / المدينة" : "Choose location — country / region / city"));

  const handlePick = (cityId) => {
    onChange?.(cityId);
    setOpen(false);
    setQuery("");
  };

  // ── Filtered city list when user searches ─────────────────────────
  const q = query.trim().toLowerCase();
  const filteredCities = q
    ? ALL_CITIES.filter((c) => c._search.includes(q)).slice(0, 60)
    : null;

  return (
    <div ref={wrapperRef} className={`relative ${compact ? "" : "w-full"}`} data-testid={testId}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-3 rounded-2xl border-2 bg-white text-start transition-colors hover:border-[#c9a55c] ${
          open ? "border-[#c9a55c]" : "border-[#0a0a0a]/10"
        } ${compact ? "px-3 py-2 text-sm" : "px-4 py-3"}`}
        data-testid={`${testId}-trigger`}
      >
        <span className="flex items-center gap-2 min-w-0">
          <MapPin className={`shrink-0 text-[#8a6f2e] ${compact ? "w-4 h-4" : "w-5 h-5"}`} />
          <span className={`truncate ${selected ? "text-[#0a0a0a] font-semibold" : "text-[#6b6b6b]"}`}>
            {buttonLabel}
          </span>
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {selected && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
              className="p-1 rounded-full hover:bg-red-100 text-[#8a8a8a] hover:text-red-600 transition-colors"
              data-testid={`${testId}-clear`}
              aria-label="clear"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-[#525252] transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute z-50 mt-2 w-full bg-white rounded-2xl border-2 border-[#0a0a0a]/10 shadow-2xl overflow-hidden"
          data-testid={`${testId}-dropdown`}
        >
          {/* Search */}
          <div className="border-b border-[#0a0a0a]/8 p-3">
            <div className="relative">
              <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-[#8a8a8a]" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={lang === "ar" ? "ابحث: مدينة، منطقة، أو دولة…" : "Search: city, region or country…"}
                className="w-full pl-3 pr-9 ltr:pl-9 ltr:pr-3 py-2 rounded-lg border border-[#0a0a0a]/15 focus:border-[#c9a55c] focus:outline-none text-sm"
                style={lang === "ar" ? { paddingInlineStart: "2.5rem", paddingInlineEnd: "0.75rem" } : {}}
                data-testid={`${testId}-search`}
              />
            </div>

            {/* Auto-detected location hint */}
            {detecting && !value && (
              <div className="mt-3 flex items-center gap-2 text-[11px] text-[#8a8a8a]" data-testid={`${testId}-detecting`}>
                <Locate className="w-3.5 h-3.5 animate-pulse text-[#8a6f2e]" />
                {lang === "ar" ? "نكتشف موقعك تلقائياً…" : "Detecting your location…"}
              </div>
            )}
            {detected && !value && !detecting && (
              <button
                type="button"
                onClick={() => handlePick(detected.id)}
                className="mt-3 w-full flex items-center justify-between gap-3 rounded-xl border-2 border-[#c9a55c]/60 bg-gradient-to-br from-[#fdf9ef] to-white p-3 hover:border-[#c9a55c] hover:shadow-sm transition-all"
                data-testid={`${testId}-geo-suggest`}
              >
                <span className="flex items-center gap-2.5 min-w-0">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#c9a55c]/15 border border-[#c9a55c]/40 shrink-0">
                    <Locate className="w-4 h-4 text-[#8a6f2e]" />
                  </span>
                  <span className="min-w-0 text-start">
                    <span className="block text-[10px] uppercase tracking-wider text-[#8a6f2e] font-bold">
                      {lang === "ar" ? "تم اكتشاف موقعك" : "We detected you in"}
                    </span>
                    <span className="block text-sm font-bold text-[#0a0a0a] truncate">
                      {detected.country.flag} {lang === "ar" ? detected.ar : detected.en} · {lang === "ar" ? detected.country.ar : detected.country.en}
                    </span>
                  </span>
                </span>
                <span className="shrink-0 text-[11px] font-bold text-[#8a6f2e] underline">
                  {lang === "ar" ? "استخدمها" : "Use it"}
                </span>
              </button>
            )}

            {/* Popular chips */}
            {!q && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`${testId}-popular`}>
                <span className="text-[10px] uppercase tracking-wider text-[#8a8a8a] me-1 self-center">
                  {lang === "ar" ? "الأكثر بحثاً:" : "Popular:"}
                </span>
                {POPULAR_IDS.map((id) => {
                  const c = findCity(id);
                  if (!c) return null;
                  return (
                    <button
                      type="button"
                      key={id}
                      onClick={() => handlePick(id)}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        value === id
                          ? "bg-[#0a0a0a] text-[#c9a55c] border-[#0a0a0a]"
                          : "bg-[#fafaf7] text-[#3a3a3a] border-[#0a0a0a]/10 hover:bg-[#fdf9ef] hover:border-[#c9a55c]"
                      }`}
                      data-testid={`${testId}-popular-${id}`}
                    >
                      <span className="me-1">{c.country.flag}</span>
                      {lang === "ar" ? c.ar : c.en}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Body */}
          <div className="max-h-[420px] overflow-y-auto" data-testid={`${testId}-list`}>
            {q ? (
              /* ── Search results (flat) ────────────────────────────── */
              filteredCities.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#8a8a8a]">
                  {lang === "ar" ? "لا توجد نتائج مطابقة" : "No matches found"}
                </div>
              ) : (
                <ul className="py-1">
                  {filteredCities.map((c) => (
                    <li key={`${c.country.code}-${c.id}`}>
                      <button
                        type="button"
                        onClick={() => handlePick(c.id)}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-[#fdf9ef] text-start transition-colors ${
                          value === c.id ? "bg-[#fdf9ef]" : ""
                        }`}
                        data-testid={`${testId}-city-${c.id}`}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="text-lg shrink-0">{c.country.flag}</span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-[#0a0a0a] truncate">
                              {lang === "ar" ? c.ar : c.en}
                            </span>
                            <span className="block text-[11px] text-[#6b6b6b] truncate">
                              {lang === "ar" ? `${c.region.ar} · ${c.country.ar}` : `${c.region.en} · ${c.country.en}`}
                            </span>
                          </span>
                        </span>
                        {value === c.id && <Check className="w-4 h-4 text-[#8a6f2e] shrink-0" />}
                      </button>
                    </li>
                  ))}
                </ul>
              )
            ) : (
              /* ── Grouped by country ────────────────────────────────── */
              <ul>
                {COUNTRIES.map((country) => (
                  <li key={country.code} className="border-b last:border-b-0 border-[#0a0a0a]/5">
                    <div
                      className="sticky top-0 bg-[#fafaf7] px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#525252] flex items-center gap-2 z-10"
                      data-testid={`${testId}-country-${country.code}`}
                    >
                      <span className="text-base leading-none">{country.flag}</span>
                      <span>{lang === "ar" ? country.ar : country.en}</span>
                      <span className="text-[10px] font-normal text-[#8a8a8a] me-auto ms-auto">
                        ({country.regions.reduce((n, r) => n + r.cities.length, 0)} {lang === "ar" ? "مدينة" : "cities"})
                      </span>
                    </div>
                    {country.regions.map((region) => (
                      <div key={region.id} className="px-3 pb-2 pt-1">
                        {country.primary && (
                          <div className="text-[10px] font-semibold text-[#8a6f2e] px-1 mb-1">
                            {lang === "ar" ? region.ar : region.en}
                          </div>
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                          {region.cities.map((c) => (
                            <button
                              type="button"
                              key={c.id}
                              onClick={() => handlePick(c.id)}
                              className={`text-start text-sm px-2.5 py-1.5 rounded-lg hover:bg-[#fdf9ef] transition-colors flex items-center justify-between gap-2 ${
                                value === c.id ? "bg-[#fdf9ef] font-semibold text-[#5a4615]" : "text-[#3a3a3a]"
                              }`}
                              data-testid={`${testId}-city-${c.id}`}
                            >
                              <span className="truncate">{lang === "ar" ? c.ar : c.en}</span>
                              {value === c.id && <Check className="w-3.5 h-3.5 text-[#8a6f2e] shrink-0" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-[#0a0a0a]/8 px-4 py-2 text-[10px] text-[#8a8a8a] flex items-center justify-between bg-[#fafaf7]">
            <span>
              {ALL_CITIES.length} {lang === "ar" ? "مدينة في" : "cities in"} {COUNTRIES.length} {lang === "ar" ? "دول" : "countries"}
            </span>
            <button
              type="button"
              onClick={() => { setOpen(false); setQuery(""); }}
              className="font-semibold text-[#8a6f2e] hover:text-[#5a4615]"
              data-testid={`${testId}-close`}
            >
              {lang === "ar" ? "إغلاق" : "Close"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
